import * as Tone from 'tone';

class AudioManager {
    constructor() {
        this.initialized = false;
        
        // 存在しなかった、あるいは読み込みに失敗したアセットを記録
        this.failedAssets = new Set();
        
        // ボリューム設定 (0.0 - 1.0)
        this.volumes = {
            master: 0.8,
            bgm: 0.6,
            se: 0.7
        };
        this.isMuted = false;

        this.loadSettings();

        // バス設定
        this.masterBus = Tone.getDestination();
        this.bgmBus = new Tone.Channel(Tone.gainToDb(this.volumes.bgm)).connect(this.masterBus);
        this.seBus = new Tone.Channel(Tone.gainToDb(this.volumes.se)).connect(this.masterBus);
        
        // 初期ボリューム反映
        this.masterBus.volume.value = Tone.gainToDb(this.volumes.master);
        this.masterBus.mute = this.isMuted;

        // BGMトラック (インゲーム用)
        this.bgmTracks = {
            base: null,
            playerIdeology: null,
            npcIdeology: null
        };
        this.bgmTrackNames = {
            base: null,
            playerIdeology: null,
            npcIdeology: null
        };
        this.bgmTrackStartTimes = {
            base: null,
            playerIdeology: null,
            npcIdeology: null
        };
        this.currentInGameSession = 0; // セッション管理用

        // 一般BGM (タイトル等)
        this.currentBgm = null;
        this.currentBgmName = null;
        this.currentCategory = null;
        this.currentBgmStartTime = null; // 再生開始時刻を記録
        
        // SEキャッシュ
        this.sePlayers = {};

        // Page Visibility API 用の状態保存
        this.savedPlaybackState = {
            currentBgm: null,
            bgmTracks: {
                base: null,
                playerIdeology: null,
                npcIdeology: null
            }
        };

        // Page Visibility API のイベントリスナーを設定
        this.setupVisibilityHandler();
    }

    setupVisibilityHandler() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.handleVisibilityHidden();
            } else {
                this.handleVisibilityVisible();
            }
        });
    }

    handleVisibilityHidden() {
        // タブ非表示時：現在の再生状態を保存して停止
        console.log('[Audio] Tab hidden, pausing audio');
        
        // 一般BGMの状態を保存
        if (this.currentBgm && this.currentBgmStartTime) {
            const now = Tone.now();
            let progress = now - this.currentBgmStartTime;
            
            // ループBGMの場合、ファイル長を考慮して実際の再生位置を計算
            if (this.currentBgm.loop && this.currentBgm.buffer) {
                const duration = this.currentBgm.buffer.duration;
                progress = progress % duration;
            }
            
            console.log(`[Audio] Saving BGM progress: ${progress}s, name: ${this.currentBgmName}`);
            this.savedPlaybackState.currentBgm = {
                name: this.currentBgmName,
                progress: progress,
                loop: this.currentBgm.loop
            };
            this.currentBgm.stop();
        }

        // インゲームBGMの状態を保存
        const now = Tone.now();
        this.savedPlaybackState.bgmTracks = {
            base: this.bgmTracks.base && this.bgmTrackStartTimes.base ? {
                name: this.bgmTrackNames.base,
                progress: this.calculateLoopedProgress(this.bgmTracks.base, now - this.bgmTrackStartTimes.base),
                volume: this.bgmTracks.base.volume.value
            } : null,
            playerIdeology: this.bgmTracks.playerIdeology && this.bgmTrackStartTimes.playerIdeology ? {
                name: this.bgmTrackNames.playerIdeology,
                progress: this.calculateLoopedProgress(this.bgmTracks.playerIdeology, now - this.bgmTrackStartTimes.playerIdeology),
                volume: this.bgmTracks.playerIdeology.volume.value
            } : null,
            npcIdeology: this.bgmTracks.npcIdeology && this.bgmTrackStartTimes.npcIdeology ? {
                name: this.bgmTrackNames.npcIdeology,
                progress: this.calculateLoopedProgress(this.bgmTracks.npcIdeology, now - this.bgmTrackStartTimes.npcIdeology),
                volume: this.bgmTracks.npcIdeology.volume.value
            } : null
        };
        console.log('[Audio] Saved in-game BGM progress:', this.savedPlaybackState.bgmTracks);

        // インゲームBGMを停止
        Object.values(this.bgmTracks).forEach(player => {
            if (player) {
                player.stop();
            }
        });

        // SEも無音化（seBusのボリュームを-∞に設定）
        this.seBus.volume.value = -Infinity;
    }

    calculateLoopedProgress(player, progress) {
        if (player.loop && player.buffer) {
            const duration = player.buffer.duration;
            return progress % duration;
        }
        return progress;
    }

    async handleVisibilityVisible() {
        // タブ復帰時：保存した状態から再生を再開
        console.log('[Audio] Tab visible, resuming audio');
        
        if (!this.initialized) {
            await this.init();
        }

        // Tone.js Contextが停止している場合は再開
        if (Tone.context.state !== 'running') {
            await Tone.context.resume();
        }

        // SEのボリュームを元に戻す
        this.seBus.volume.value = Tone.gainToDb(this.volumes.se);

        // 一般BGMの再開
        if (this.savedPlaybackState.currentBgm) {
            const { name, progress, loop } = this.savedPlaybackState.currentBgm;
            console.log(`[Audio] Resuming BGM from progress: ${progress}s, name: ${name}`);
            try {
                const url = `./assets/sounds/bgm/${name}.wav`;
                const player = new Tone.Player().connect(this.bgmBus);
                player.loop = loop;
                player.fadeIn = 0.5;
                player.fadeOut = 0.5;
                
                await player.load(url);
                
                if (this.currentBgmName === name) {
                    // 保存した進行位置から再生（seekを使用）
                    player.start();
                    if (progress > 0) {
                        player.seek(progress);
                    }
                    this.currentBgm = player;
                } else {
                    player.dispose();
                }
            } catch (e) {
                console.error(`[Audio] Failed to resume BGM: ${name}.wav`, e);
            }
            this.savedPlaybackState.currentBgm = null;
        }

        // インゲームBGMの再開
        const hasTracks = this.savedPlaybackState.bgmTracks.base || 
                          this.savedPlaybackState.bgmTracks.playerIdeology || 
                          this.savedPlaybackState.bgmTracks.npcIdeology;
        
        if (hasTracks) {
            const loadTrack = async (savedTrack, targetKey) => {
                if (!savedTrack) return null;
                
                console.log(`[Audio] Loading track ${targetKey} from progress: ${savedTrack.progress}s`);
                try {
                    const url = `./assets/sounds/bgm/${savedTrack.name}.wav`;
                    const player = new Tone.Player().connect(this.bgmBus);
                    player.loop = true;
                    
                    await player.load(url);
                    
                    // まだ再生は開始しない
                    player.volume.value = savedTrack.volume;
                    
                    return { player, progress: savedTrack.progress };
                } catch (e) {
                    console.error(`[Audio] Failed to load track: ${savedTrack.name}.wav`, e);
                    return null;
                }
            };

            // すべてのトラックを並列でロード
            const [baseData, playerData, npcData] = await Promise.all([
                loadTrack(this.savedPlaybackState.bgmTracks.base, 'base'),
                loadTrack(this.savedPlaybackState.bgmTracks.playerIdeology, 'playerIdeology'),
                loadTrack(this.savedPlaybackState.bgmTracks.npcIdeology, 'npcIdeology')
            ]);

            // すべてのロードが完了した後、同じタイミングで再生開始
            const now = Tone.now() + 0.1;
            
            if (baseData) {
                baseData.player.start(now, baseData.progress);
                this.bgmTracks.base = baseData.player;
                this.bgmTrackStartTimes.base = now - baseData.progress;
            }
            if (playerData) {
                playerData.player.start(now, playerData.progress);
                this.bgmTracks.playerIdeology = playerData.player;
                this.bgmTrackStartTimes.playerIdeology = now - playerData.progress;
            }
            if (npcData) {
                npcData.player.start(now, npcData.progress);
                this.bgmTracks.npcIdeology = npcData.player;
                this.bgmTrackStartTimes.npcIdeology = now - npcData.progress;
            }

            this.savedPlaybackState.bgmTracks = {
                base: null,
                playerIdeology: null,
                npcIdeology: null
            };
        }
    }

    loadSettings() {
        try {
            const saved = localStorage.getItem('historio_audio_settings');
            if (saved) {
                const settings = JSON.parse(saved);
                this.volumes = { ...this.volumes, ...settings.volumes };
                this.isMuted = settings.isMuted ?? false;
            }
        } catch (e) {
            console.error("Failed to load audio settings", e);
        }
    }

    saveSettings() {
        try {
            const settings = {
                volumes: this.volumes,
                isMuted: this.isMuted
            };
            localStorage.setItem('historio_audio_settings', JSON.stringify(settings));
        } catch (e) {
            console.error("Failed to save audio settings", e);
        }
    }

    async init() {
        if (this.initialized) return;
        try {
            await Tone.start();
            this.initialized = true;
            console.log("AudioManager initialized");
        } catch (e) {
            console.error("Failed to initialize Tone.js", e);
        }
    }

    // 音量設定
    setVolume(type, value) {
        this.volumes[type] = value;
        const db = Tone.gainToDb(value);
        
        if (type === 'master') this.masterBus.volume.value = db;
        if (type === 'bgm') this.bgmBus.volume.value = db;
        if (type === 'se') { 
            this.seBus.volume.value = db; 
            this.playSE('select'); // 設定変更時に音を再生してフィードバック
        }
        
        this.saveSettings();
    }

    setMute(mute) {
        this.isMuted = mute;
        this.masterBus.mute = mute;
        this.saveSettings();
    }

    toggleMute() {
        this.setMute(!this.isMuted);
    }

    /**
     * SEを再生する
     * @param {string} name ファイル名 (拡張子抜き)
     */
    async playSE(name) {
        // 未初期化なら初期化を試みる
        if (!this.initialized) await this.init();
        if (!this.initialized || this.failedAssets.has(name)) return;

        try {
            if (!this.sePlayers[name]) {
                const url = `./assets/sounds/se/${name}.mp3`;
                const player = new Tone.Player().connect(this.seBus);
                
                // 読み込みに失敗した場合は例外が飛ぶ
                await player.load(url);
                this.sePlayers[name] = player;
            }
            
            // 重複再生を確実にするため、一度止めてから再生
            if (this.sePlayers[name].buffer.loaded) {
                this.sePlayers[name].stop();
                this.sePlayers[name].start();
            }
        } catch (e) {
            console.warn(`[Audio] SE asset missing: ${name}.mp3`);
            this.failedAssets.add(name);
        }
    }

    /**
     * 通常のBGM（タイトル等）を再生
     */
    async playBGM(name, loop = true) {
        if (!this.initialized) await this.init();
        if (!this.initialized) return;
        
        // すでに同じ曲が再生中（またはロード中）なら何もしない
        if (this.currentBgmName === name) return;

        // インゲーム再生を停止
        this.stopInGameTracks();

        // 違う曲をリクエストされたので、現在の曲は即座に停止する
        if (this.currentBgm) {
            this.currentBgm.stop();
            this.currentBgm.dispose();
            this.currentBgm = null;
        }

        // 名前を更新
        this.currentBgmName = name;

        if (this.failedAssets.has(name)) return;

        try {
            // .wav を優先しつつ、将来的な拡張性も考慮
            const url = `./assets/sounds/bgm/${name}.wav`;
            const player = new Tone.Player().connect(this.bgmBus);
            player.loop = loop;
            player.fadeIn = 1;
            player.fadeOut = 1;
            
            await player.load(url);
            
            if (this.currentBgmName === name) {
                player.start();
                this.currentBgm = player;
                this.currentBgmStartTime = Tone.now(); // 再生開始時刻を記録
            } else {
                player.dispose();
            }
        } catch (e) {
            console.error(`[Audio] BGM decode error: ${name}.wav. Ensure 16-bit PCM format.`, e);
            this.failedAssets.add(name);
        }
    }

    /**
     * 指定されたBGMが流れていない場合のみ再生する
     */
    async ensureBGM(name, loop = true) {
        if (this.currentBgmName === name && this.currentBgm) return;
        return this.playBGM(name, loop);
    }

    /**
     * カテゴリに基づいたBGM管理
     * @param {string} category BGMCategoryのいずれか
     * @param {Object} options 追加オプション（勝利/敗北判定など）
     */
    ensureCategoryBGM(category, options = {}) {
        this.currentCategory = category;
        
        if (category === 'BASIC') {
            this.ensureBGM('title');
        } else if (category === 'YONOTSUNE') {
            this.ensureBGM('yonotsune');
        } else if (category === 'RESULT') {
            const name = options.isVictory ? 'victory' : 'defeat';
            this.ensureBGM(name, true); 
        }
    }

    /**
     * インゲームBGMの開始（3トラック同時再生）
     */
    async startInGameBGM(baseName, playerIdeologyName, npcIdeologyName) {
        // 新しいセッションを開始（古いロード処理を無効化するため）
        const sessionId = ++this.currentInGameSession;
        console.log(`[Audio] Starting in-game BGM sequence (Session: ${sessionId}): ${baseName}`);
        
        if (!this.initialized) {
            await this.init();
        }
        
        if (!this.initialized || sessionId !== this.currentInGameSession) return;

        // 確実にContextを開始
        if (Tone.context.state !== 'running') {
            await Tone.context.resume();
        }
        
        this.stopBGM();
        // stopBGMでリセットされたsessionIdを復元（この呼び出し自体が新しいセッションなので）
        this.currentInGameSession = sessionId;

        // トラック名を保存
        this.bgmTrackNames = {
            base: baseName,
            playerIdeology: playerIdeologyName,
            npcIdeology: npcIdeologyName
        };

        const loadTrack = async (name) => {
            if (!name || sessionId !== this.currentInGameSession) return null;
            if (this.failedAssets.has(name)) return null;

            try {
                const url = `./assets/sounds/bgm/${name}.wav`;
                const player = new Tone.Player().connect(this.bgmBus);
                player.loop = true;
                player.volume.value = -Infinity;
                
                await player.load(url);
                
                // ロード完了後にセッションが有効か再確認
                if (sessionId !== this.currentInGameSession) {
                    player.dispose();
                    return null;
                }
                return player;
            } catch (e) {
                console.error(`[Audio] Decode error in loadTrack: ${name}.wav`, e);
                this.failedAssets.add(name);
                return null;
            }
        };

        try {
            // 並列ロードですべてのトラックを同時に読み込む
            const [base, player, npc] = await Promise.all([
                loadTrack(baseName),
                loadTrack(playerIdeologyName),
                loadTrack(npcIdeologyName)
            ]);

            // セッションが有効か再確認
            if (sessionId !== this.currentInGameSession) {
                base?.dispose();
                player?.dispose();
                npc?.dispose();
                return;
            }

            this.bgmTracks.base = base;
            this.bgmTracks.playerIdeology = player;
            this.bgmTracks.npcIdeology = npc;

            // すべてのトラックを同じタイミングで再生開始
            const now = Tone.now() + 0.5;
            
            if (this.bgmTracks.base) {
                this.bgmTracks.base.start(now);
                this.bgmTracks.base.volume.rampTo(0, 1.5, now);
                this.bgmTrackStartTimes.base = now; // 再生開始時刻を記録
            }
            if (this.bgmTracks.playerIdeology) {
                this.bgmTracks.playerIdeology.start(now);
                this.bgmTrackStartTimes.playerIdeology = now;
            }
            if (this.bgmTracks.npcIdeology) {
                this.bgmTracks.npcIdeology.start(now);
                this.bgmTrackStartTimes.npcIdeology = now;
            }

            console.log(`[Audio] In-game BGM sequence started (Session: ${sessionId})`);
        } catch (e) {
            console.error("[Audio] Critical failure in startInGameBGM", e);
        }
    }

    /**
     * イデオロギー状況に応じてBGMのトラックを切り替える（フェード）
     */
    updateInGameBGM(hasPlayerIdeology, hasNPCIdeology) {
        if (!this.initialized) return;
        
        const fadeTime = 1.5;
        const now = Tone.now();
        
        const updateTrackVolume = (player, isActive, label) => {
            if (!player || !player.buffer.loaded) return;
            
            player.volume.cancelScheduledValues(now);
            player.volume.setValueAtTime(player.volume.value, now);
            player.volume.rampTo(isActive ? 0 : -Infinity, fadeTime, now);
        };

        updateTrackVolume(this.bgmTracks.playerIdeology, hasPlayerIdeology, 'PlayerIdeology');
        updateTrackVolume(this.bgmTracks.npcIdeology, hasNPCIdeology, 'NPCIdeology');
    }

    stopInGameTracks() {
        // セッションを無効化
        this.currentInGameSession++;
        
        Object.values(this.bgmTracks).forEach(player => {
            if (player) {
                try {
                    player.stop();
                    player.dispose();
                } catch (e) {
                    console.warn("[Audio] Error disposing track", e);
                }
            }
        });
        this.bgmTracks = { base: null, playerIdeology: null, npcIdeology: null };
        this.bgmTrackNames = { base: null, playerIdeology: null, npcIdeology: null };
        this.bgmTrackStartTimes = { base: null, playerIdeology: null, npcIdeology: null };
    }

    stopBGM() {
        if (this.currentBgm) {
            this.currentBgm.stop();
            this.currentBgm.dispose();
            this.currentBgm = null;
        }
        this.currentBgmName = null;
        this.currentBgmStartTime = null;
        this.stopInGameTracks();
    }
}

const audioManager = new AudioManager();
export default audioManager;
